/**
 * ============================================================================
 * VOCALIRA CORE CONVERSATION ENGINE (VCCE) - Conversation State Machine
 * ============================================================================
 * 
 * Máquina de estados explícita para o fluxo de conversação.
 * 
 * Cada transição é:
 * - Validada (apenas transições permitidas ocorrem)
 * - Logada (auditoria completa)
 * - Observável (callbacks para UI e telemetria)
 * 
 * @author Vocalira Team
 * @version 2.0.0 - VCCE Architecture
 */

// ============================================================================
// DEFINIÇÃO DE ESTADOS
// ============================================================================

/**
 * Estados do pipeline de conversação.
 * Cada estado tem um propósito claro e transições bem definidas.
 */
export const ConversationState = {
  // Estados de espera
  IDLE: 'IDLE',                     // Sistema parado, aguardando input
  READY: 'READY',                   // Pronto para próximo turno (após resposta)
  
  // Estados de captura de áudio
  LISTENING: 'LISTENING',           // Microfone aberto, aguardando fala
  VOICE_DETECTED: 'VOICE_DETECTED', // Voz detectada, capturando
  
  // Estados de transcrição
  TRANSCRIBING: 'TRANSCRIBING',     // STT processando (interim results)
  TRANSCRIPT_FINAL: 'TRANSCRIPT_FINAL', // STT finalizou (final result)
  
  // Estados de decisão
  EVALUATING: 'EVALUATING',         // Decision Engine avaliando
  
  // Estados de processamento
  PROCESSING: 'PROCESSING',         // Preparando request para LLM
  
  // Estados de geração
  LLM_STREAMING: 'LLM_STREAMING',   // LLM gerando resposta
  TTS_GENERATING: 'TTS_GENERATING', // TTS gerando áudio
  
  // Estados de reprodução
  SPEAKING: 'SPEAKING',             // Reproduzindo áudio de Lira
  
  // Estados especiais
  INTERRUPTED: 'INTERRUPTED',       // Usuário interrompeu Lira
  ERROR: 'ERROR',                   // Erro no pipeline
  
  // Estados de continuação (modo contínuo)
  WAITING_TO_LISTEN: 'WAITING_TO_LISTEN', // Pausa antes de auto-listen
};

/**
 * Labels em português para exibição na UI.
 */
export const StateLabels = {
  IDLE: '',
  READY: 'Pronta para conversar',
  LISTENING: 'Sua vez de falar...',
  VOICE_DETECTED: 'Ouvindo...',
  TRANSCRIBING: 'Transcrevendo...',
  TRANSCRIPT_FINAL: 'Entendi!',
  EVALUATING: 'Processando...',
  PROCESSING: 'Preparando resposta...',
  LLM_STREAMING: 'Pensando...',
  TTS_GENERATING: 'Gerando áudio...',
  SPEAKING: 'Lira está falando...',
  INTERRUPTED: 'Interrompido',
  ERROR: 'Erro',
  WAITING_TO_LISTEN: 'Preparando...',
};

/**
 * Transições válidas entre estados.
 * Formato: ESTADO_ATUAL -> [ESTADOS_PERMITIDOS]
 */
const VALID_TRANSITIONS = {
  [ConversationState.IDLE]: [
    ConversationState.LISTENING,
    ConversationState.READY,
  ],
  
  [ConversationState.READY]: [
    ConversationState.LISTENING,
    ConversationState.IDLE,
  ],
  
  [ConversationState.LISTENING]: [
    ConversationState.VOICE_DETECTED,
    ConversationState.IDLE,
    ConversationState.ERROR,
    ConversationState.EVALUATING, // Timeout sem voz
  ],
  
  [ConversationState.VOICE_DETECTED]: [
    ConversationState.TRANSCRIBING,
    ConversationState.LISTENING,    // Falso positivo
    ConversationState.EVALUATING,   // Decisão rápida
    ConversationState.IDLE,
    ConversationState.ERROR,
  ],
  
  [ConversationState.TRANSCRIBING]: [
    ConversationState.TRANSCRIPT_FINAL,
    ConversationState.EVALUATING,
    ConversationState.VOICE_DETECTED, // Usuário continuou falando
    ConversationState.IDLE,
    ConversationState.ERROR,
  ],
  
  [ConversationState.TRANSCRIPT_FINAL]: [
    ConversationState.EVALUATING,
    ConversationState.PROCESSING,   // Decisão imediata
    ConversationState.TRANSCRIBING, // Mais fala detectada
    ConversationState.IDLE,
  ],
  
  [ConversationState.EVALUATING]: [
    ConversationState.PROCESSING,
    ConversationState.LISTENING,    // Aguardar mais input
    ConversationState.IDLE,
    ConversationState.ERROR,
  ],
  
  [ConversationState.PROCESSING]: [
    ConversationState.LLM_STREAMING,
    ConversationState.ERROR,
    ConversationState.IDLE,
  ],
  
  [ConversationState.LLM_STREAMING]: [
    ConversationState.TTS_GENERATING,
    ConversationState.SPEAKING,      // TTS inline
    ConversationState.INTERRUPTED,
    ConversationState.ERROR,
    ConversationState.IDLE,
  ],
  
  [ConversationState.TTS_GENERATING]: [
    ConversationState.SPEAKING,
    ConversationState.INTERRUPTED,
    ConversationState.ERROR,
    ConversationState.IDLE,
  ],
  
  [ConversationState.SPEAKING]: [
    ConversationState.READY,
    ConversationState.WAITING_TO_LISTEN,
    ConversationState.INTERRUPTED,
    ConversationState.IDLE,
  ],
  
  [ConversationState.INTERRUPTED]: [
    ConversationState.LISTENING,
    ConversationState.PROCESSING,
    ConversationState.IDLE,
  ],
  
  [ConversationState.ERROR]: [
    ConversationState.IDLE,
    ConversationState.LISTENING,
    ConversationState.READY,
  ],
  
  [ConversationState.WAITING_TO_LISTEN]: [
    ConversationState.LISTENING,
    ConversationState.IDLE,
  ],
};

// ============================================================================
// CLASSE PRINCIPAL: ConversationStateMachine
// ============================================================================

/**
 * Máquina de estados para controle do fluxo de conversação.
 * 
 * Responsabilidades:
 * - Gerenciar transições de estado
 * - Validar transições
 * - Emitir eventos para observadores
 * - Registrar histórico para auditoria
 */
class ConversationStateMachine {
  constructor(options = {}) {
    // Estado atual
    this.currentState = ConversationState.IDLE;
    this.previousState = null;
    
    // Histórico para auditoria
    this.history = [];
    this.maxHistorySize = options.maxHistorySize || 100;
    
    // Timestamps
    this.stateEnteredAt = Date.now();
    this.conversationStartedAt = null;
    
    // Callbacks
    this.onStateChange = options.onStateChange || (() => {});
    this.onInvalidTransition = options.onInvalidTransition || (() => {});
    this.onLog = options.onLog || console.log;
    
    // Métricas
    this.metrics = {
      totalTransitions: 0,
      invalidTransitions: 0,
      stateTimings: {},
      transitionCounts: {},
    };
    
    // Contexto adicional (metadata)
    this.context = {
      transcript: '',
      error: null,
      interruptReason: null,
    };
    
    this._log('STATE_MACHINE_INITIALIZED', { initialState: this.currentState });
  }
  
  // ==========================================================================
  // TRANSIÇÕES
  // ==========================================================================
  
  /**
   * Tenta fazer uma transição para um novo estado.
   * @param {string} newState - Estado destino
   * @param {Object} metadata - Dados adicionais da transição
   * @returns {boolean} Se a transição foi bem-sucedida
   */
  transition(newState, metadata = {}) {
    const now = Date.now();
    const timeInCurrentState = now - this.stateEnteredAt;
    
    // Validar transição
    if (!this._isValidTransition(newState)) {
      this.metrics.invalidTransitions++;
      
      const error = {
        from: this.currentState,
        to: newState,
        metadata,
        timestamp: now,
      };
      
      this._log('INVALID_TRANSITION', error);
      this.onInvalidTransition(error);
      
      return false;
    }
    
    // Registrar tempo no estado anterior
    this._recordStateTiming(this.currentState, timeInCurrentState);
    
    // Executar transição
    this.previousState = this.currentState;
    this.currentState = newState;
    this.stateEnteredAt = now;
    this.metrics.totalTransitions++;
    
    // Registrar transição
    const transitionKey = `${this.previousState}->${newState}`;
    this.metrics.transitionCounts[transitionKey] = 
      (this.metrics.transitionCounts[transitionKey] || 0) + 1;
    
    // Atualizar contexto
    if (metadata.transcript !== undefined) {
      this.context.transcript = metadata.transcript;
    }
    if (metadata.error !== undefined) {
      this.context.error = metadata.error;
    }
    
    // Adicionar ao histórico
    const historyEntry = {
      from: this.previousState,
      to: newState,
      timestamp: now,
      timeInPreviousState: timeInCurrentState,
      metadata,
    };
    
    this.history.push(historyEntry);
    
    // Limitar tamanho do histórico
    if (this.history.length > this.maxHistorySize) {
      this.history.shift();
    }
    
    // Log
    this._log('STATE_TRANSITION', {
      from: this.previousState,
      to: newState,
      timeInPreviousState: timeInCurrentState,
      metadata,
    });
    
    // Notificar observadores
    this.onStateChange({
      previousState: this.previousState,
      currentState: this.currentState,
      metadata,
      timeInPreviousState: timeInCurrentState,
    });
    
    return true;
  }
  
  /**
   * Verifica se uma transição é válida.
   * @param {string} newState - Estado destino
   * @returns {boolean}
   */
  _isValidTransition(newState) {
    // Mesmo estado é sempre válido (no-op)
    if (newState === this.currentState) {
      return true;
    }
    
    const allowedTransitions = VALID_TRANSITIONS[this.currentState];
    return allowedTransitions && allowedTransitions.includes(newState);
  }
  
  // ==========================================================================
  // AÇÕES DE CONVENIÊNCIA
  // ==========================================================================
  
  /**
   * Inicia uma nova sessão de escuta.
   */
  startListening() {
    if (this.currentState === ConversationState.IDLE || 
        this.currentState === ConversationState.READY ||
        this.currentState === ConversationState.WAITING_TO_LISTEN) {
      this.conversationStartedAt = Date.now();
      return this.transition(ConversationState.LISTENING, { action: 'start_listening' });
    }
    return false;
  }
  
  /**
   * Marca detecção de voz.
   */
  voiceDetected() {
    return this.transition(ConversationState.VOICE_DETECTED, { action: 'voice_detected' });
  }
  
  /**
   * Marca início de transcrição.
   * @param {string} interimTranscript - Texto parcial
   */
  transcribing(interimTranscript = '') {
    return this.transition(ConversationState.TRANSCRIBING, { 
      action: 'transcribing',
      transcript: interimTranscript 
    });
  }
  
  /**
   * Marca transcrição finalizada.
   * @param {string} finalTranscript - Texto final
   */
  transcriptFinal(finalTranscript) {
    return this.transition(ConversationState.TRANSCRIPT_FINAL, {
      action: 'transcript_final',
      transcript: finalTranscript
    });
  }
  
  /**
   * Marca início de avaliação de decisão.
   */
  evaluating() {
    return this.transition(ConversationState.EVALUATING, { action: 'evaluating' });
  }
  
  /**
   * Marca início de processamento.
   * @param {string} transcript - Texto a processar
   */
  processing(transcript) {
    return this.transition(ConversationState.PROCESSING, {
      action: 'processing',
      transcript
    });
  }
  
  /**
   * Marca início de streaming do LLM.
   */
  llmStreaming() {
    return this.transition(ConversationState.LLM_STREAMING, { action: 'llm_streaming' });
  }
  
  /**
   * Marca início de geração de TTS.
   */
  ttsGenerating() {
    return this.transition(ConversationState.TTS_GENERATING, { action: 'tts_generating' });
  }
  
  /**
   * Marca início de reprodução de áudio.
   */
  speaking() {
    return this.transition(ConversationState.SPEAKING, { action: 'speaking' });
  }
  
  /**
   * Marca interrupção.
   * @param {string} reason - Motivo da interrupção
   */
  interrupted(reason = 'user_interrupt') {
    this.context.interruptReason = reason;
    return this.transition(ConversationState.INTERRUPTED, {
      action: 'interrupted',
      reason
    });
  }
  
  /**
   * Marca erro.
   * @param {Error|string} error - Erro ocorrido
   */
  error(error) {
    this.context.error = error instanceof Error ? error.message : error;
    return this.transition(ConversationState.ERROR, {
      action: 'error',
      error: this.context.error
    });
  }
  
  /**
   * Volta ao estado pronto.
   */
  ready() {
    return this.transition(ConversationState.READY, { action: 'ready' });
  }
  
  /**
   * Volta ao estado idle.
   */
  idle() {
    this.conversationStartedAt = null;
    return this.transition(ConversationState.IDLE, { action: 'idle' });
  }
  
  /**
   * Entra no estado de espera para auto-listen.
   * @param {number} delay - Delay antes de auto-listen
   */
  waitingToListen(delay = 500) {
    return this.transition(ConversationState.WAITING_TO_LISTEN, {
      action: 'waiting_to_listen',
      delay
    });
  }
  
  // ==========================================================================
  // QUERIES
  // ==========================================================================
  
  /**
   * Verifica se está em um estado de "escuta ativa".
   */
  isListening() {
    return [
      ConversationState.LISTENING,
      ConversationState.VOICE_DETECTED,
      ConversationState.TRANSCRIBING,
    ].includes(this.currentState);
  }
  
  /**
   * Verifica se está processando.
   */
  isProcessing() {
    return [
      ConversationState.PROCESSING,
      ConversationState.LLM_STREAMING,
      ConversationState.TTS_GENERATING,
    ].includes(this.currentState);
  }
  
  /**
   * Verifica se Lira está falando.
   */
  isSpeaking() {
    return this.currentState === ConversationState.SPEAKING;
  }
  
  /**
   * Verifica se está em estado de erro.
   */
  isError() {
    return this.currentState === ConversationState.ERROR;
  }
  
  /**
   * Verifica se pode iniciar nova escuta.
   */
  canStartListening() {
    return [
      ConversationState.IDLE,
      ConversationState.READY,
      ConversationState.WAITING_TO_LISTEN,
      ConversationState.ERROR,
    ].includes(this.currentState);
  }
  
  /**
   * Retorna o label do estado atual.
   */
  getStateLabel() {
    return StateLabels[this.currentState] || '';
  }
  
  // ==========================================================================
  // MÉTRICAS E AUDITORIA
  // ==========================================================================
  
  /**
   * Registra o tempo gasto em um estado.
   */
  _recordStateTiming(state, duration) {
    if (!this.metrics.stateTimings[state]) {
      this.metrics.stateTimings[state] = {
        totalTime: 0,
        count: 0,
        min: Infinity,
        max: 0,
      };
    }
    
    const timing = this.metrics.stateTimings[state];
    timing.totalTime += duration;
    timing.count++;
    timing.min = Math.min(timing.min, duration);
    timing.max = Math.max(timing.max, duration);
  }
  
  /**
   * Retorna métricas completas.
   */
  getMetrics() {
    const result = { ...this.metrics };
    
    // Calcular médias
    result.averageStateTimings = {};
    for (const [state, timing] of Object.entries(this.metrics.stateTimings)) {
      result.averageStateTimings[state] = timing.count > 0 
        ? Math.round(timing.totalTime / timing.count)
        : 0;
    }
    
    return result;
  }
  
  /**
   * Retorna histórico de transições.
   */
  getHistory() {
    return [...this.history];
  }
  
  /**
   * Retorna histórico formatado para debug.
   */
  getHistoryFormatted() {
    return this.history.map(entry => 
      `[${new Date(entry.timestamp).toISOString()}] ${entry.from} -> ${entry.to} (${entry.timeInPreviousState}ms)`
    ).join('\n');
  }
  
  /**
   * Limpa o histórico.
   */
  clearHistory() {
    this.history = [];
  }
  
  /**
   * Reseta métricas.
   */
  resetMetrics() {
    this.metrics = {
      totalTransitions: 0,
      invalidTransitions: 0,
      stateTimings: {},
      transitionCounts: {},
    };
  }
  
  // ==========================================================================
  // LOGGING
  // ==========================================================================
  
  _log(event, data = {}) {
    const entry = {
      timestamp: Date.now(),
      event: `[StateMachine] ${event}`,
      currentState: this.currentState,
      data
    };
    
    this.onLog(entry.event, entry);
  }
}

export default ConversationStateMachine;
export { VALID_TRANSITIONS };
